/**
 * Login Component
 *
 * This component handles user authentication, including email input and OTP verification.
 *
 * TODO: Implement rate limiting for OTP attempts
 * TODO: Implement proper logout mechanism to clear stored tokens
 */

import React, { useRef, useContext, useEffect, useState } from "react";

import * as z from "zod";
import dayjs from "dayjs";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { WarningTooltip } from "@/taskpane/utils/tooltips";
import { AuthContext } from "@/taskpane/contexts/auth/AuthContext";
import InputCode from "@/taskpane/components/input-code/InputCode";
import LoadingButton from "@/taskpane/components/button/LoadingButton";
import LoadingButtonSepteo from "@/taskpane/components/button/LoadingButtonSepteo";
import { Box, Button, Card, Divider, Stack, TextField, Typography } from "@mui/material";
import { initiateAuth, respondToAuthChallenge } from "@/taskpane/services/auth";
import { enqueueConfirmSnackbar } from "@/taskpane/components/snackbar-provider/SnackbarProvider";
import { SnackbarCloseIcon } from "@/taskpane/components/snackbar-provider";
import { useOfficeContext } from "@/taskpane/contexts/office/office-context";
import { usePrevious } from "@uidotdev/usehooks";
import { clearSenstiveDataFromLocalStorage } from "@/taskpane/utils/storage";
import { initiateSSO, generateState } from "@/taskpane/services/sso";
import styles from "./login-view.styles";

const MAX_OTP_ATTEMPTS = 3;
const OTP_COOLDOWN_PERIOD = 300000; // 5 minutes in milliseconds

interface KeyDownEvent extends React.KeyboardEvent<HTMLInputElement> {
  key: string;
}

function getOtpCode(element: any) {
  // Select all the elements with the class 'otp-td'
  const otpElements = element.querySelectorAll(".otp-td");

  // Initialize an empty string to hold the OTP code
  let otpCode = "";

  // Check if otpElements is not null or empty
  if (otpElements && otpElements.length > 0) {
    // Loop through each element and concatenate its text content
    otpElements.forEach((el: { textContent: string }) => {
      if (el && el.textContent) {
        // Check if element and its textContent exist
        otpCode += el.textContent;
      }
    });
  } else {
    console.error("OTP elements not found.");
    return null; // Return null if no elements are found
  }

  // Return the final OTP code
  return otpCode || null; // Return null if OTP code remains empty
}

/**
 * Zod schema for validating login form input.
 *
 * This schema ensures that the email field is:
 * - A string
 * - A valid email address
 * - Not empty
 *
 * @constant
 * @type {z.ZodObject}
 * @property {z.ZodString} email - The email field, must be a valid email address and is required.
 * @throws Will throw validation errors if the input does not meet the specified criteria.
 */
const loginSchema = z.object({
  email: z.string().email("Addresse mail invalide").min(1, "L'adresse mail est requise"),
});

type FormData = z.infer<typeof loginSchema>;

/**
 * Login component for handling user authentication.
 *
 * This component manages the login process, including email submission and OTP verification.
 * It uses React hooks for state management and various helper functions for handling authentication.
 *
 * @component
 * @returns {JSX.Element} The rendered login component.
 */
export function LoginPage() {
  const [authMethod, setAuthMethod] = useState<'email' | 'sso' | null>(null);
  const [username, setUsername] = useState(() => localStorage.getItem("username") || "");
  const [showResend, setShowResend] = useState(false);
  const [session, setSession] = useState<string | undefined>(
    () => localStorage.getItem("session") || null || undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [lastOtpAttemptTimestamp, setLastOtpAttemptTimestamp] = useState<number>();
  const [otp, setOtp] = useState("");
  const [defaultOtp, setDefaultOtp] = useState("");
  const previousDefaultOtp = usePrevious(defaultOtp);
  const { setTokens: login } = useContext(AuthContext);
  const { changeCounter, currentMailbox, isDesktop } = useOfficeContext();
  const loginFormDefaults: FormData = {
    email: currentMailbox?.userProfile?.emailAddress ?? "",
  };
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // Vérifier si le paramètre crpcen est présent dans l'URL
  const hasCrpcenParam = React.useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Cas 2: crpcen après le hash (#/login?crpcen=xxx)
    const hash = window.location.hash;
    const queryStringIndex = hash.indexOf('?');
    const hashParams = queryStringIndex !== -1 
      ? new URLSearchParams(hash.substring(queryStringIndex + 1))
      : new URLSearchParams();
    
    const hasCrpcen = searchParams.has('crpcen') || hashParams.has('crpcen');
    return hasCrpcen;
  }, []);

  const {
    handleSubmit, // function to handle form submission
    formState: { errors }, // Destructure errors
    control,
    watch, // Watch form values for real-time validation
  } = useForm<FormData>({
    // Call useForm hook with generic type FormData
    resolver: zodResolver(loginSchema), // Pass Zod schema to resolver
    defaultValues: loginFormDefaults, // specify default values for form inputs
    mode: "onChange", // Validate on every change to trigger real-time validation for button color
  });
  
  // Watch the email field to determine if it's valid
  const emailValue = watch("email");
  // Only consider email valid if it has content and is a proper email format
  const isEmailValid = emailValue && emailValue.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  const timeoutRef = useRef<any | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Detect SSO OAuth2 callback parameters and redirect to callback page
   * This handles the case where OAuth2 provider redirects to base URL with query params
   * instead of directly to the hash route #/auth/sso/callback
   * 
   * Note: With HashRouter, query params before the hash (e.g., ?code=xxx#/login) 
   * are not available in location.search, so we need to use window.location.search
   */
  useEffect(() => {
    // Check if we're in a reset state (coming from error callback)
    const isReset = sessionStorage.getItem('sso_reset');
    if (isReset) {
      console.log('[Login] SSO reset flag detected, clearing flag and not redirecting');
      sessionStorage.removeItem('sso_reset');
      
      // Clean up URL if it still has query parameters
      if (window.location.search) {
        const baseUrl = window.location.href.split('?')[0];
        window.history.replaceState(null, '', baseUrl);
      }
      return;
    }
    
    // OAuth callback detection: GenAPI redirects to base URL with OAuth parameters
    // This is the correct approach because OAuth2 providers don't support hash fragments in redirect URIs
    // We detect the parameters and route internally to the callback handler
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (code && state) {
      console.log('[Login] OAuth callback detected, routing to callback handler');
      navigate(`/auth/sso/callback${window.location.search}`);
      return;
    }
    
    // Check if user is switching office via tenant_id - auto-trigger SSO (SAME AS CRPCEN)
    const targetTenantId = localStorage.getItem('target_office_tenant_id');
    const ssoTriggered = sessionStorage.getItem('sso_auto_triggered');
    
    if (targetTenantId && !ssoTriggered) {
      // Set flag to prevent re-triggering on page reload
      sessionStorage.setItem('sso_auto_triggered', 'true');
      
      // Small delay to ensure the UI renders before redirect
      setTimeout(() => {
        if (isDesktop) {
          const sessionId = generateState();
          initiateSSO(sessionId);
        } else {
          initiateSSO();
        }
      }, 500);
      
      return; // Don't check targetCRPCEN if we have target_office_tenant_id
    }

    // Check if user is switching CRPCEN - auto-trigger SSO
    const targetCRPCEN = localStorage.getItem('target_office_crpcen');
    const targetOfficeName2 = localStorage.getItem('target_office_name');
    const ssoTriggered2 = sessionStorage.getItem('sso_auto_triggered');
    
    if (targetCRPCEN && targetOfficeName2 && !ssoTriggered2) {
      // Set flag to prevent re-triggering on page reload
      sessionStorage.setItem('sso_auto_triggered', 'true');
      
      // Small delay to ensure the UI renders before redirect
      setTimeout(() => {
        if (isDesktop) {
          const sessionId = generateState();
          initiateSSO(sessionId);
        } else {
          initiateSSO();
        }
      }, 500);
    }
  }, [navigate, isDesktop]);

  /**
   * Handles the submission of the username (email) form for initiating authentication.
   *
   * @async
   * @function handleOnUserNameSubmit
   * @param {FormData} data - The form data containing the email.
   */
  const handleOnUserNameSubmit = async (data: FormData) => {
    const resultEmail = data.email.toLowerCase();

    // Show validation errors after submit attempt
    setShowValidationErrors(true);

    // Check if email is valid before proceeding
    if (!isEmailValid) {
      return; // Don't proceed if email is invalid
    }

    setIsLoading(true);

    try {
      const { result, statusCode } = await initiateAuth(resultEmail);

      if (statusCode === 200) {
        const sessionExpOn = dayjs().add(2, 'day').valueOf();

        setSession(result.Session);

        localStorage.setItem("session", result.Session);
        localStorage.setItem("username", resultEmail);
        localStorage.setItem('sessionExpOn', sessionExpOn.toString());

        setOtpAttempts(0);
        setUsername(resultEmail);
        setLastOtpAttemptTimestamp(undefined);
        setShowValidationErrors(false); // Reset validation errors on success
      } else if (statusCode === 400) {
        enqueueSnackbar("L'adresse mail est invalide", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Une erreur est survenue. Veuillez réessayer.", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the submission of the OTP code for verifying 2FA authentication.
   *
   * @async
   * @function handleConfirmOTP
   * @param {string} otpValue - The OTP code entered by the user.
   */
  const handleConfirmOTP = async (otpValue: string) => {
    const currentTime = Date.now();
    if (otpAttempts >= MAX_OTP_ATTEMPTS && currentTime - lastOtpAttemptTimestamp! < OTP_COOLDOWN_PERIOD) {
      const remainingCooldown = Math.ceil((OTP_COOLDOWN_PERIOD - (currentTime - lastOtpAttemptTimestamp!)) / 60000);
      enqueueConfirmSnackbar(
        `Trop de tentatives. Veuillez réessayer dans ${remainingCooldown} minutes.`,
        () => {},
        "OK",
        {
          persist: true,
          maxHideDuration: remainingCooldown,
          variant: "error",
          action: (snackbarId: SnackbarKey | undefined) => (
            <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />
          ),
        }
      );
      return;
    }

    setIsLoading(true);
    try {
      const { result, statusCode } = await respondToAuthChallenge(username, otpValue, session);
      if (statusCode === 200) {
        login(result.AuthenticationResult);
        navigate("/folders");
      } else if (statusCode === 400) {
        setOtpAttempts((prevAttempts) => prevAttempts + 1);
        setLastOtpAttemptTimestamp(Date.now());
        enqueueSnackbar("Le code n'est pas valide", {
          variant: "error",
          action: (snackbarId: SnackbarKey | undefined) => (
            <>
              <Button
                color="error"
                variant="text"
                size="small"
                onClick={() => {
                  handleOnUserNameSubmit({ email: username });
                  setOtp("");
                }}
                sx={{ marginRight: 5 }}
              >
                Nouveau code
              </Button>
              <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />
            </>
          ),
        });
      }
    } catch (error) {
      console.error("Challenge response failed:", error);
      enqueueSnackbar("Une erreur est survenue. Veuillez réessayer.", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the search for an OTP code in the email body.
   * This function will help to detect OTP code in when mail item changes and
   * automaticaly fill the OTP input. This is a TODO feature.
   * @function handleSearchForOTP
   * @param {string} mailContext - The email context containing the email body and other usefull infos
   * that can help to detect OTP code in when mail item changes.
   */
  const handleSearchForOTP = (mailContext: any) => {
    return new Promise((resolve) => {
      if (!mailContext?.item) {
        resolve(null);
        return;
      }

      let { body } = mailContext.item;

      body.getAsync(Office.CoercionType.Html, function (asyncResult: any) {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const bodyHtml = asyncResult.value;

          const element = document.createElement("div");
          element.innerHTML = bodyHtml;

          const otpElement = getOtpCode(element);

          if (otpElement) {
            setDefaultOtp(otpElement);
            setShowResend(false);
          } else {
            // console.log('No OTP code found.');
          }
        }
      });
    });
  };

  function isExpirationValid() {
    const storedDate = localStorage.getItem('expirationDate');
    if (!storedDate) {
      return false;
    }

    const now = dayjs().valueOf();
    return now < parseInt(storedDate, 10);
  }

  useEffect(() => {
    if (location?.pathname === "/login" && session) {
      handleSearchForOTP(currentMailbox);
    }
  }, [changeCounter, currentMailbox]);

  useEffect(() => {
    // Check for saved session data
    const savedUsername = localStorage.getItem("username");
    const savedSession = localStorage.getItem("session");
    if (savedUsername && savedSession) {
      if (isExpirationValid()) {
        setUsername(savedUsername);
        setSession(savedSession);
      } else {
        clearSenstiveDataFromLocalStorage()
      }
    }
  }, []);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowResend(true);
    }, 30000);
    return () => {
      // Clear the timeout when component unmounts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [otp]);

  /**
   * Handle SSO login button click
   * 
   * Desktop Outlook: Opens browser for auth, redirects to polling view
   * Web/Mobile: Direct OAuth redirect (callback returns to same context)
   */
  const handleSSOLogin = () => {
    try {
      // Check if we're in Desktop Outlook (cross-context scenario)
      // Access platform directly from Office.context
      let isDesktopOutlook = false;
      
      if (typeof Office !== 'undefined' && Office.context?.platform !== undefined) {
        const platform = Office.context.platform;
        isDesktopOutlook = platform === Office.PlatformType.PC || 
                          platform === Office.PlatformType.Mac;
        
        console.log('[Login] Platform detected:', {
          platform: platform,
          isDesktop: isDesktopOutlook
        });
      } else {
        console.log('[Login] Office context not available, assuming web context');
      }
      
      if (isDesktopOutlook) {
        console.log('[Login] Desktop Outlook detected - using cross-context SSO flow');
        
        // Generate session ID for polling
        const sessionId = generateState();
        localStorage.setItem('sso_session_id', sessionId);
        
        // Store session ID as OAuth state (will be used for token storage)
        // Note: sessionStorage is used here for temporary state within the add-in context
        sessionStorage.setItem('sso_state', sessionId);
        
        console.log('[Login] Generated session ID for Desktop Outlook:', sessionId.substring(0, 10) + '...', {
          stored_in_localStorage: 'sso_session_id',
          stored_in_sessionStorage: 'sso_state',
          will_be_used_for: 'polling and OAuth state'
        });
        
        // Open browser for auth - pass the session ID so it's used as OAuth state
        // This ensures the session ID used for polling matches the OAuth state
        initiateSSO(sessionId);
        
        // Redirect to polling view
        navigate('/auth/sso/polling');
        
      } else {
        // Web or mobile - normal OAuth flow
        console.log('[Login] Web/Mobile context - using standard SSO flow');
        initiateSSO();
      }
      
    } catch (error) {
      console.error('[Login] Error initiating SSO:', error);
      enqueueSnackbar('Erreur lors de la connexion SSO', { variant: 'error' });
    }
  };



  return (
    <Box sx={styles.container}>
      <Card sx={styles.card}>
        <Stack spacing={2} p={2} alignItems="center">
          {/* Authentication method selection */}
          {!session && !authMethod && (
            <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
              <Typography sx={styles.title} variant="subtitle1">
                Connexion à votre compte
              </Typography>
              
              {/* SSO Button */}
              <LoadingButtonSepteo
                onClick={handleSSOLogin}
                variant="contained"
                size="medium"
                sx={styles.button}
                fullWidth
                isEmailValid={true}
              >
                S'authentifier avec mon compte Septeo
              </LoadingButtonSepteo>
              
              {/* Afficher le bouton Bylaw uniquement si crpcen n'est pas dans l'URL */}
              {!hasCrpcenParam && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', my: 1 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      ou
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Stack>
                  
                  {/* Email/OTP Button */}
                  <Button
                    onClick={() => setAuthMethod('email')}
                    variant="outlined"
                    size="medium"
                    sx={styles.button}
                    fullWidth
                  >
                    S'authentifier avec mon compte Bylaw
                  </Button>
                </>
              )}
            </Stack>
          )}

          {/* Email/OTP Login Form */}
          {!session && authMethod === 'email' && (
            <form
              onSubmit={handleSubmit(handleOnUserNameSubmit)}
              style={styles.form}
            >
              <Typography sx={styles.title} variant="subtitle1">
                Connexion par email
              </Typography>

              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={showValidationErrors && !!errors.email}
                    fullWidth
                    helperText={showValidationErrors ? errors.email?.message : ""}
                    label="Adresse email"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      // Reset validation errors when user starts typing again
                      if (showValidationErrors) {
                        setShowValidationErrors(false);
                      }
                    }}
                    size="small"
                    type="text"
                    variant="outlined"
                    margin="normal"
                    sx={styles.textField}
                  />
                )}
                control={control}
                defaultValue={loginFormDefaults.email}
                name="email"
              />

              <WarningTooltip title={showValidationErrors && errors.email?.message ? "Veuillez entrer une adresse email" : ""}>
                <span>
                  <LoadingButtonSepteo
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    className="login-button"
                    loading={isLoading}
                    size="medium"
                    sx={styles.button}
                    isEmailValid={!!isEmailValid}
                  >
                    Se connecter
                  </LoadingButtonSepteo>
                </span>
              </WarningTooltip>

              <Button
                onClick={() => setAuthMethod(null)}
                variant="text"
                size="small"
                sx={{ mt: 1 }}
              >
                ← Retour
              </Button>
            </form>
          )}

          {session && (
            <Stack sx={styles.otpSection}>
              <Typography sx={styles.title} variant="subtitle1">
                Vous allez recevoir un code de sécurité par mail
              </Typography>

              <InputCode
                length={4}
                onCodeChange={(code) => setOtp(code)}
                handleAltConfirmOTP={handleConfirmOTP}
                defaultOtp={defaultOtp}
                previousDefaultOtp={previousDefaultOtp}
              />
              <WarningTooltip title={otp.length !== 4 ? "Veuillez entrer le code de sécurité reçu par mail" : ""}>
                <span>
                  <LoadingButton
                    disabled={otp.length !== 4}
                    loading={isLoading}
                    onClick={() => {
                      handleConfirmOTP(otp);
                      setShowResend(false);
                    }}
                    sx={styles.button}
                  >
                    Valider
                  </LoadingButton>
                </span>
              </WarningTooltip>
              {showResend ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => loginFormDefaults?.email !== "" && handleOnUserNameSubmit(loginFormDefaults)}
                  sx={styles.resendButton}
                >
                  Vous n'avez pas reçu de code ? Cliquez ici
                </Button>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Card>
    </Box>
  );
}
